import React from "react";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    InputLabel,
    List,
    MenuItem,
    Select,
    ListItem,
    ListItemText,
    Paper,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import moment from "moment";
import CronExpressionBuilder from "../../components/CronExpressionBuilder";
import CustomTags from "../../components/CustomTags";

export default function BrTabIdea({
    formData,
    onFormChange,
    onGenHiddenWordsearchChange,
    onGenWordWheelChange,
    onGenStandaloneImageChange,
    onGenWordSearch9Change,
    genHiddenWordsearchLoading = false,
    genWordWheelLoading = false,
    genStandaloneImageLoading = false,
    genWordSearch9Loading = false,
    onCronChange,
    onTagsChange,
    onSubmit,
    onCancel,
    onDelete,
    isEdit,
    availableTags,
    loadingTags,
    qualityStats
}) {
    return (
        <form onSubmit={onSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 600 }}>
                <TextField
                    autoFocus
                    label="Title"
                    fullWidth
                    variant="outlined"
                    name="title"
                    value={formData.title}
                    onChange={onFormChange}
                    required
                />

                <CronExpressionBuilder
                    value={formData.cron}
                    onChange={onCronChange}
                />

                <TextField
                    label="Idea"
                    fullWidth
                    variant="outlined"
                    name="idea"
                    value={formData.idea}
                    onChange={onFormChange}
                    multiline
                    rows={4}
                    placeholder={
                        formData.genStandaloneImage
                            ? "Describe a visual theme for beautiful standalone images (landscapes, animals, cakes…). No story words."
                            : formData.genWordWheel
                            ? "Describe a theme for Word Wheel puzzles. Words will be 3–8 letters and share letters (e.g. lady, lay, day)."
                            : undefined
                    }
                    helperText={
                        formData.genStandaloneImage
                            ? "Standalone images only: generates beautiful scene images for sliding/jigsaw (DRAFT). No crossword or story narrative."
                            : formData.genWordWheel
                            ? "Word Wheel only: generates draft Word Wheel templates (3–8 letter words that cross on an 8×8 grid). No grid or crossword puzzles."
                            : undefined
                    }
                />

                <CustomTags
                    value={formData.tags}
                    onChange={onTagsChange}
                    options={availableTags}
                    loading={loadingTags}
                    margin="dense"
                />

                <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                    <FormControlLabel
                        control={
                            <Switch
                                name="roundRobin"
                                checked={formData.roundRobin}
                                onChange={onFormChange}
                            />
                        }
                        label="Continuous"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                name="enabled"
                                checked={formData.enabled}
                                onChange={onFormChange}
                            />
                        }
                        label="Enabled"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="isKids"
                                checked={formData.isKids}
                                onChange={onFormChange}
                            />
                        }
                        label="Is Kids"
                    />
                </Box>

                {isEdit && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    name="exhausted"
                                    checked={formData.exhausted || false}
                                    onChange={onFormChange}
                                />
                            }
                            label="Exhausted"
                        />
                        {formData.exhausted && formData.exhaustedAt ? (
                            <Typography variant="caption" color="text.secondary">
                                Marked exhausted {moment(formData.exhaustedAt).format("YYYY-MM-DD HH:mm")}
                            </Typography>
                        ) : null}
                        <Typography variant="caption" color="text.secondary">
                            When exhausted, Continuous is turned off and schedulers skip this idea. Turn off and save to allow generation again.
                        </Typography>
                    </Box>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                            <Checkbox
                                name="genHiddenWordsearch"
                                checked={formData.genHiddenWordsearch}
                                onChange={onGenHiddenWordsearchChange || onFormChange}
                                disabled={
                                    genHiddenWordsearchLoading
                                    || formData.genWordWheel
                                    || formData.genStandaloneImage
                                    || formData.genWordSearch9
                                }
                            />
                        }
                        label="Generate a draft hidden message or word search"
                    />
                    <FormControlLabel
                        sx={{ m: 0, alignItems: "flex-start" }}
                        control={
                            <Checkbox
                                name="genWordWheel"
                                checked={formData.genWordWheel}
                                onChange={onGenWordWheelChange || onFormChange}
                                disabled={genWordWheelLoading || formData.genStandaloneImage || formData.genWordSearch9}
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2">Generate Word Wheel puzzles only</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                    3–8 letter words, minimum 3 words, shared letters (e.g. LADY, LAY, DAY)
                                </Typography>
                            </Box>
                        }
                    />
                    <FormControlLabel
                        sx={{ m: 0, alignItems: "flex-start" }}
                        control={
                            <Checkbox
                                name="genWordSearch9"
                                checked={Boolean(formData.genWordSearch9)}
                                onChange={onGenWordSearch9Change || onFormChange}
                                disabled={
                                    genWordSearch9Loading
                                    || formData.genStandaloneImage
                                    || formData.genWordWheel
                                    || formData.genHiddenWordsearch
                                }
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2">Generate Word Search Mini only</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                    Random Mini board 6×6–9×9 from the word set (words 4–9 letters, overlap to fill). No hidden message or crossword.
                                </Typography>
                            </Box>
                        }
                    />
                    <FormControlLabel
                        sx={{ m: 0, alignItems: "flex-start" }}
                        control={
                            <Checkbox
                                name="genStandaloneImage"
                                checked={formData.genStandaloneImage}
                                onChange={onGenStandaloneImageChange || onFormChange}
                                disabled={genStandaloneImageLoading}
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2">Generate standalone images only</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                    Beautiful scenes for sliding/jigsaw — no narrative story or word puzzles
                                </Typography>
                            </Box>
                        }
                    />
                    <FormControl size="small" sx={{ minWidth: 280, mt: 1 }}>
                        <InputLabel id="bedrock-image-style-label">Image style</InputLabel>
                        <Select
                            labelId="bedrock-image-style-label"
                            name="imageStyle"
                            label="Image style"
                            value={formData.imageStyle || ""}
                            onChange={onFormChange}
                        >
                            <MenuItem value="">Off</MenuItem>
                            <MenuItem value="photo-v2">Photo v2 (6 pictures)</MenuItem>
                        </Select>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                            Separate from standalone images. Photo v2 does not change standalone generation.
                        </Typography>
                    </FormControl>
                </Box>

                {isEdit ? (
                    <ImageQualityPanel qualityStats={qualityStats} />
                ) : null}

                <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap" }}>
                    <Button type="button" variant="outlined" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        {isEdit ? "Save" : "Create"}
                    </Button>
                    {isEdit && onDelete && (
                        <Button type="button" variant="outlined" color="error" onClick={onDelete}>
                            Delete
                        </Button>
                    )}
                </Box>
            </Box>
        </form>
    );
}

function rejectChipColor(reason) {
    const r = String(reason || "").toUpperCase();
    if (r === "DELETED") return "error";
    if (r === "DISABLED") return "warning";
    if (r === "DISCARDED") return "default";
    return "default";
}

function ImageQualityPanel({ qualityStats }) {
    const created = Number(qualityStats?.standaloneImagesCreated) || 0;
    const rejected = Number(qualityStats?.imagesRejected) || 0;
    const items = Array.isArray(qualityStats?.rejectedImages) ? qualityStats.rejectedImages : [];
    const rate = created > 0 ? Math.round((rejected / created) * 100) : 0;
    let summaryColor = "text.secondary";
    if (rejected > 0 && rate >= 50) {
        summaryColor = "error.main";
    } else if (rejected > 0 && rate >= 25) {
        summaryColor = "warning.main";
    }

    return (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Image quality
            </Typography>
            <Typography variant="body2" sx={{ color: summaryColor, mb: items.length ? 1 : 0 }}>
                {created} created, {rejected} rejected{created > 0 ? ` (${rate}%)` : ""}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: items.length ? 0.5 : 0 }}>
                Deleted or disabled standalone images count against this idea.
            </Typography>
            {items.length > 0 ? (
                <List dense disablePadding>
                    {items.map((row, index) => (
                        <ListItem key={`${row.subject || "item"}-${row.createdAt || index}`} disableGutters sx={{ py: 0.25 }}>
                            <ListItemText
                                primary={row.subject || "(no subject)"}
                                secondary={row.createdAt ? moment(row.createdAt).format("YYYY-MM-DD HH:mm") : null}
                                primaryTypographyProps={{ variant: "body2" }}
                                secondaryTypographyProps={{ variant: "caption" }}
                            />
                            {row.reason ? (
                                <Chip
                                    size="small"
                                    label={row.reason}
                                    color={rejectChipColor(row.reason)}
                                    variant="outlined"
                                    sx={{ ml: 1, flexShrink: 0 }}
                                />
                            ) : null}
                        </ListItem>
                    ))}
                </List>
            ) : null}
        </Paper>
    );
}
